
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, Clock, MapPin, User } from "lucide-react";
import { EventWithAttendees } from "@/services/eventService";
import { useAuth } from "@/hooks/useAuth";
import { format } from "date-fns";
import ParticipantsList from "./ParticipantsList";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface EventCardProps {
  event: EventWithAttendees;
  onRequestAttendance: (eventId: string) => void;
  onViewDetails: (event: EventWithAttendees) => void;
  isRequesting?: boolean;
}

const EventCard = ({ event, onRequestAttendance, onViewDetails, isRequesting }: EventCardProps) => {
  const { user } = useAuth();
  const [creatorName, setCreatorName] = useState<string>("Event Creator");
  const [adminNames, setAdminNames] = useState<string[]>([]);
  
  const userAttendance = event.attendees.find(a => a.user_id === user?.id);
  const approvedAttendees = event.attendees.filter(a => a.status === "approved");
  const isCreator = event.created_by === user?.id;

  useEffect(() => {
    const fetchCreatorAndAdmins = async () => {
      try {
        // Fetch creator's name
        const { data: creatorProfile } = await supabase
          .from("user_profiles")
          .select("display_name, first_name, last_name")
          .eq("user_id", event.created_by)
          .single();

        if (creatorProfile) {
          const name = creatorProfile.display_name || 
                      `${creatorProfile.first_name || ''} ${creatorProfile.last_name || ''}`.trim() || 
                      'Event Creator';
          setCreatorName(name);
        }

        // Fetch admin names (excluding the creator)
        const adminAttendees = event.attendees.filter(a => a.is_admin && a.user_id !== event.created_by);
        const adminUserIds = adminAttendees.map(a => a.user_id);

        if (adminUserIds.length > 0) {
          const { data: adminProfiles } = await supabase
            .from("user_profiles")
            .select("display_name, first_name, last_name")
            .in("user_id", adminUserIds);

          if (adminProfiles) {
            const names = adminProfiles.map(profile => 
              profile.display_name || 
              `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 
              'Admin'
            );
            setAdminNames(names);
          }
        }
      } catch (error) {
        console.error("Error fetching creator and admin names:", error);
      }
    };

    fetchCreatorAndAdmins();
  }, [event.created_by, event.attendees]);

  const getStatusBadge = () => {
    if (isCreator) {
      return <Badge variant="default">Creator</Badge>;
    }
    if (userAttendance) {
      const variant = userAttendance.status === "approved" ? "default" : 
                    userAttendance.status === "pending" ? "secondary" : "destructive";
      return <Badge variant={variant}>{userAttendance.status}</Badge>;
    }
    return null;
  };

  const canRequestAttendance = !userAttendance && !isCreator;

  const getHostedByText = () => {
    let text = `Hosted by ${creatorName}`;
    if (adminNames.length > 0) {
      text += ` and ${adminNames.join(", ")}`;
    }
    return text;
  };

  return (
    <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 w-full" onClick={() => onViewDetails(event)}>
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Event Image */}
          {event.image_url && (
            <div className="w-full md:w-64 h-48 md:h-auto overflow-hidden rounded-t-lg md:rounded-l-lg md:rounded-t-none">
              <img
                src={event.image_url}
                alt={event.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}
          
          {/* Event Content */}
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between gap-2 mb-4">
              <div className="flex-1">
                <h3 className="font-bold text-xl mb-1">{event.title}</h3>
                {event.description && (
                  <p className="text-gray-600 mb-2">{event.description}</p>
                )}
                <p className="text-sm text-gray-500">
                  {getHostedByText()}
                </p>
              </div>
              {getStatusBadge()}
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-500 mb-4">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{format(new Date(event.event_date), "MMM dd, yyyy")}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{event.event_time}</span>
              </div>
              
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span className="line-clamp-1">{event.address}</span>
              </div>
            </div>

            {/* Participants List */}
            <div className="mb-4">
              <ParticipantsList attendees={approvedAttendees} />
            </div>

            {canRequestAttendance && (
              <Button 
                onClick={(e) => {
                  e.stopPropagation();
                  onRequestAttendance(event.id);
                }}
                disabled={isRequesting}
                className="w-full md:w-auto"
              >
                {isRequesting ? "Requesting..." : "Request to Attend"}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default EventCard;
